import * as React from 'react';
import { useState, useEffect } from 'react';
import { Typography } from '@mui/material';
import { getData } from '../../services/api.ts';
import { extractUnit, DESC_MAP } from '../../utils/utils.ts';

export type BaseChartProps = {
  table: string;
  valuename: string;
  timeField: string;
  measurements: string[];
  timeFrom: string;
  timeTo: string;
  unit: string;
  osmName: string;
};

export default function BaseChartVisualization({
  table, valuename, timeField, measurements, timeFrom, timeTo, unit, osmName,
}: BaseChartProps) {
  const [sensorData, setSensorData] = useState<Record<string, number | string[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const shortUnit = extractUnit(unit);

  useEffect(() => {
    Promise.all(
      measurements.map((name) =>
        getData(table, timeField, timeFrom, timeTo, name, osmName).then((data) => ({
          name,
          data,
        }))
      )
    )
      .then((results) => {
        const mappedData: Record<string, number | string[]> = {};
        results.forEach(({ name, data }) => {
          mappedData[name] = data;
        });
        setSensorData(mappedData);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [table, timeField, timeFrom, timeTo, measurements, osmName]);

  if (loading) return <Typography>Loading...</Typography>;
  if (error) return <Typography color='error'>{error}</Typography>;

  const firstDataset = sensorData[measurements[0]];
  if (!firstDataset || !firstDataset.length)
    return <Typography>No data found</Typography>;

  const xLabels = firstDataset.map((item) => new Date(item.bucket).toLocaleString());

  const series = measurements
    .filter((name) => sensorData[name]?.length)
    .map((name) => {
      const data = sensorData[name].map((item) => parseFloat(item[valuename]));
      const rawName = sensorData[name][0]?.name || name;
      const osmName = sensorData[name][0]?.osm_name || '';
      const label = `${DESC_MAP[rawName] ?? rawName}${osmName ? ` (${osmName})` : ''}`;
      return {
        data,
        label,
        showMark: false,
        valueFormatter: (v: number | null) => (v !== null ? `${v.toFixed(2)} ${shortUnit}` : ''),
      };
    });

  return { series, xLabels, shortUnit };
}
